function getScopeRoot() {
    return document.querySelector('[data-family-guard-scope]') || null;
}

function isInScope(el) {
    const root = getScopeRoot();
    return !!(root && el instanceof Element && root.contains(el));
}

function getPicker() {
    const root = getScopeRoot();
    return root ? root.querySelector('custom-picker') : null;
}

function hasPicker() {
    return !!getPicker();
}

function getAllGroups() {
    const picker = getPicker();
    if (!picker) return [];
    return Array.from(picker.querySelectorAll('[data-custom-option][data-opt-key]'));
}

function isGroupRequired(groupEl) {
    if (!groupEl) return false;
    if (groupEl.hidden) return false;

    const cs = window.getComputedStyle(groupEl);
    if (cs.display === 'none' || cs.visibility === 'hidden') return false;

    return true;
}

function isGroupSelected(groupEl) {
    if (!groupEl) return true;

    const optKey = (groupEl.getAttribute('data-opt-key') || '').trim();
    if (!optKey) return true;

    return !!groupEl.querySelector(`.custom-pill.is-active[data-opt-key="${CSS.escape(optKey)}"]`);
}

function getMissingGroups() {
    const groups = getAllGroups();
    const missing = [];

    groups.forEach((group) => {
        if (!isGroupRequired(group)) return;
        if (!isGroupSelected(group)) missing.push(group);
    });

    return missing;
}


function getPickerOptions() {
    const picker = getPicker();
    if (!picker) return [];

    const el = picker.querySelector('script[data-custom-options-json]');
    if (!el) return [];

    try {
        const arr = JSON.parse(el.textContent || '[]');
        return Array.isArray(arr) ? arr : [];
    } catch (e) {
        console.warn('[CustomPickerGuard] options json parse failed', e);
        return [];
    }
}

function getGroupByOptKey(optKey) {
    const picker = getPicker();
    if (!picker) return null;
    return picker.querySelector(`[data-custom-option][data-opt-key="${CSS.escape(optKey)}"]`);
}

function getGroupWarningEl(groupEl) {
    return groupEl?.querySelector('[data-warning-opt]') || null;
}

function isOptionSelected(optKey) {
    const group = getGroupByOptKey(optKey);
    if (!group) return true; 
    return !!group.querySelector(`.custom-pill.is-active[data-opt-key="${CSS.escape(optKey)}"]`);
}

function isColorRequiredOnPage() {
    const root = getScopeRoot();
    return !!root?.querySelector('[data-family-color-picker]');
}

function isColorReady() {
    const root = getScopeRoot();
    if (!root) return true;

    const fieldset = root.querySelector('[data-family-color-picker]');
    if (!fieldset) return true;

    const checked = fieldset.querySelector('input[type="radio"]:checked');
    if (checked) return true;

    const current = fieldset.querySelector('input[type="radio"][data-current-checked="true"]');
    if (current) return true;

    return false;
}

function getColorWarningEl() {
    const picker = getPicker();
    if (!picker) return null;
    return picker.querySelector('[data-warning-color]') || null;
}

function getSelectAllMessages() {
    const root = getScopeRoot();
    if (!root) return [];
    return Array.from(root.querySelectorAll('[data-family-select-all-message]'));
}

// The summary message sitting directly above Add to cart. The button is styled
// inactive but stays clickable precisely so this can be shown — a hard-disabled
// button swallows the click and explains nothing.
function setSelectAllMessage(visible) {
    getSelectAllMessages().forEach((el) => {
        el.hidden = !visible;
    });
}

function hideAllWarnings() {
    const picker = getPicker();
    if (picker) picker.querySelectorAll('[data-warning-opt]').forEach((el) => (el.hidden = true));

    const cw = getColorWarningEl();
    if (cw) cw.hidden = true;

    setSelectAllMessage(false);
}

function validateSelections({ reveal = false, scroll = true } = {}) {
    const picker = getPicker();
    const has = !!picker;

    const missingGroups = has ? getMissingGroups() : [];

    const needColor = isColorRequiredOnPage() && !isColorReady();
    const ok = missingGroups.length === 0 && !needColor;

    const alreadyRevealed = document.documentElement.dataset.familyGuardRevealed === 'true';
    if (reveal) document.documentElement.dataset.familyGuardRevealed = 'true';

    if (!reveal && !alreadyRevealed) {
        hideAllWarnings();
        return ok;
    }

    // Past this point warnings have been revealed at least once, so the buyer has
    // already tried to buy. Key the message on whether the selection is still
    // incomplete — NOT on `reveal`. Using `reveal` hid the message again on the very
    // next picker click, leaving a greyed button with nothing explaining it.
    setSelectAllMessage(!ok);

    if (picker) picker.querySelectorAll('[data-warning-opt]').forEach((el) => (el.hidden = true));

    missingGroups.forEach((group) => {
        const w = getGroupWarningEl(group);
        if (w) w.hidden = false;
    });

    const cw = getColorWarningEl();
    if (cw) cw.hidden = !needColor;

    if (!ok && scroll) {
        const firstWarn = missingGroups.length ? getGroupWarningEl(missingGroups[0]) : null;
        const firstVisible =
            firstWarn && !firstWarn.hidden ? firstWarn : (cw && !cw.hidden ? cw : null);

        firstVisible?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }

    return ok;
}

function patchCustomElement(tagName, patchFn) {
    const Ctor = customElements.get(tagName);
    if (!Ctor || Ctor.__familyGuardPatched) return false;
    if (patchFn(Ctor) === false) return false;
    Ctor.__familyGuardPatched = true;
    return true;
}

function installPatches() {
    patchCustomElement('add-to-cart-component', (Ctor) => {
        const orig = Ctor.prototype.handleClick;
        if (typeof orig !== 'function') return false;

        Ctor.prototype.handleClick = function (event) {
            if (isInScope(this) && hasPicker()) {
                const ok = validateSelections({ reveal: true, scroll: true });
                if (!ok) {
                    event?.preventDefault?.();
                    return;
                }
            }
            return orig.call(this, event);
        };
    });

    patchCustomElement('product-form-component', (Ctor) => {
        const orig = Ctor.prototype.handleSubmit;
        if (typeof orig !== 'function') return false;

        Ctor.prototype.handleSubmit = function (event) {
            if (isInScope(this) && hasPicker()) {
                const ok = validateSelections({ reveal: true, scroll: true });
                if (!ok) {
                    event?.preventDefault?.();
                    event?.stopPropagation?.();
                    event?.stopImmediatePropagation?.();
                    return;
                }
            }
            return orig.call(this, event);
        };
    });

    // NOTE: sticky-add-to-cart is deliberately NOT patched here. Its
    // handleAddToCartClick is a class FIELD, not a prototype method, so
    // Ctor.prototype.handleAddToCartClick is undefined and this patch silently
    // never installed — it read as protection while doing nothing.
    //
    // The sticky bar proxies its click to the main add-to-cart button, so the
    // add-to-cart-component patch above is what actually validates it. Honest
    // success feedback is enforced in sticky-add-to-cart.js itself.
}

function isAcceleratedCheckoutTarget(target) {
    if (!(target instanceof Element)) return false;

    return !!(
        target.closest('.accelerated-checkout-block') ||
        target.closest('.shopify-payment-button') ||
        target.closest('.shopify-payment-button__button') ||
        target.closest('[data-shopify="payment-button"]') ||
        target.closest('shopify-accelerated-checkout') ||
        target.closest('shopify-buy-it-now-button')
    );
}

function installClickGuards() {
    if (document.documentElement.dataset.familyCheckoutGuardInstalled === '1') return;
    document.documentElement.dataset.familyCheckoutGuardInstalled = '1';

    const guardHandler = (e) => {
        const t = e.target instanceof Element ? e.target : null;
        if (!t) return;
        if (!isInScope(t)) return;
        if (!isAcceleratedCheckoutTarget(t)) return;

        const ok = validateSelections({ reveal: true, scroll: true });
        if (ok) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation?.();
    };

    document.addEventListener('pointerdown', guardHandler, true);
    document.addEventListener('mousedown', guardHandler, true);
    document.addEventListener('touchstart', guardHandler, true);
    document.addEventListener('click', guardHandler, true);

    document.addEventListener(
        'keydown',
        (e) => {
            const t = e.target instanceof Element ? e.target : null;
            if (!t) return;
            if (!isInScope(t)) return;
            if (!isAcceleratedCheckoutTarget(t)) return;
            if (e.key !== 'Enter' && e.key !== ' ') return;

            const ok = validateSelections({ reveal: true, scroll: true });
            if (ok) return;

            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation?.();
        },
        true
    );
}

function installColorTracking() {
    if (document.documentElement.dataset.familyColorTracking === '1') return;
    document.documentElement.dataset.familyColorTracking = '1';

    const mark = () => {
        document.documentElement.dataset.familyColorInteracted = 'true';
        if (document.documentElement.dataset.familyGuardRevealed === 'true') {
            validateSelections({ reveal: false, scroll: false });
        }
    };

    document.addEventListener(
        'click',
        (e) => {
            const t = e.target instanceof Element ? e.target : null;
            if (!t) return;
            if (!isInScope(t)) return;
            if (t.closest('[data-family-color-picker]')) mark();
        },
        true
    );

    document.addEventListener(
        'change',
        (e) => {
            const t = e.target instanceof Element ? e.target : null;
            if (!t) return;
            if (!isInScope(t)) return;
            if (t.closest('[data-family-color-picker]')) mark();
        },
        true
    );
}

// Advertises that this guard is live and its click-time validation is installed.
// custom-options.js will only downgrade a lock to the soft, clickable form while
// this is set — otherwise an unfinished selection stays genuinely disabled, so a
// browser where the guard died can never submit an unresolved purchase.
function publishEnforcementState() {
    const was = document.documentElement.dataset.familyGuardEnforcing === '1';
    const now = corePatchesInstalled();

    if (now) {
        document.documentElement.dataset.familyGuardEnforcing = '1';
    } else {
        delete document.documentElement.dataset.familyGuardEnforcing;
    }

    // Announce the TRANSITION, not the lifting of a server lock. custom-options.js
    // settles purchase state in connectedCallback, before this guard has certified
    // itself, so it must be told to re-settle once enforcement is available.
    //
    // This used to fire only when a family-parent lock was lifted. Child products
    // carry no lock marker, so on a child page it never fired at all — and a child
    // whose option metafields are incomplete would sit there hard-disabled forever,
    // with no message explaining why. Keying off enforcement covers every page.
    if (was !== now) {
        document.dispatchEvent(new CustomEvent('family:guard-ready'));
    }
}

function corePatchesInstalled() {
    // The two components whose click/submit handlers actually gate a purchase.
    // (sticky-add-to-cart proxies its click to the main button, so it is not
    // required here — and its handler is a class field the patch can't wrap.)
    return !!(
        customElements.get('add-to-cart-component')?.__familyGuardPatched &&
        customElements.get('product-form-component')?.__familyGuardPatched
    );
}

function syncFamilyParentButtons() {
    // Parents ship with the add-to-cart button disabled in the HTML so that a
    // buyer whose JS hasn't loaded (or failed) can never purchase the ambiguous
    // parent product. Only unlock once this guard is running, the picker markup
    // exists, AND the purchase-path patches are verifiably installed — if
    // product-form.js never loads, the button stays locked (fail closed).
    //
    // The marker is FLIPPED, never removed. Removing it (the previous behaviour)
    // threw away the only handle on these buttons: the guard could not re-assert
    // the lock if conditions later regressed, and the MutationObserver below
    // filters on this very attribute, so it went deaf at the same moment.
    const root = getScopeRoot();
    if (!root) return;

    publishEnforcementState();

    const canEnforce = hasPicker() && corePatchesInstalled();

    root.querySelectorAll('[data-family-parent-locked]').forEach((btn) => {
        // Liquid ships the marker with no value, so "locked" is anything that is
        // not the explicit "0" this guard writes when it certifies enforcement.
        const locked = btn.getAttribute('data-family-parent-locked') !== '0';

        if (canEnforce && locked) {
            btn.setAttribute('data-family-parent-locked', '0');

            // Lift ONLY the family lock. custom-options.js disables these same
            // buttons for an incomplete picker selection and marks that intent
            // separately; clearing it here would re-open the JH-2048-2 window,
            // because this runs on a timer and on every DOM mutation in scope.
            //
            // A "soft" picker lock must stay clickable so it can explain itself.
            btn.disabled = btn.getAttribute('data-picker-locked') === '1';

            // Lifting a server lock also changes what the picker may do, so ask it
            // to re-settle. (publishEnforcementState above covers the boot-time
            // transition; this covers a lock lifted later, e.g. after a morph.)
            document.dispatchEvent(new CustomEvent('family:guard-ready'));
        } else if (!canEnforce && !locked) {
            // Regression (e.g. a section morph replaced the patched component):
            // re-assert the lock rather than leaving a live, unguarded button.
            btn.setAttribute('data-family-parent-locked', '1');
            btn.disabled = true;
        }
    });
}

function boot() {
    if (!getScopeRoot()) return;

    installColorTracking();
    installPatches();
    installClickGuards();
    syncFamilyParentButtons();

    // Section re-renders (e.g. after a color change) restore the server-rendered
    // disabled state; re-sync whenever locked buttons reappear. The marker now
    // persists, so this observer keeps its handle for the life of the page.
    const lockObserver = new MutationObserver(() => syncFamilyParentButtons());
    lockObserver.observe(getScopeRoot(), {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-family-parent-locked'],
    });

    document.addEventListener('custom-picker:change', () => {
        validateSelections({ reveal: false, scroll: false });
    });

    document.addEventListener('family:change', () => {
        validateSelections({ reveal: false, scroll: false });
    });

    window.addEventListener('popstate', () => {
        const picker = getPicker();
        picker?.applyStateFromUrl?.({ replace: false });
        setTimeout(() => validateSelections({ reveal: false, scroll: false }), 0);
    });

    hideAllWarnings();
}

boot();

const patchTimer = setInterval(() => {
    installPatches();
    syncFamilyParentButtons();

    if (corePatchesInstalled()) clearInterval(patchTimer);
}, 200);